export const AUDIO_TEXT_MAX_CHARACTERS = 8_000;
export const AUDIO_MAX_SEGMENTS = 120;
export const AUDIO_MAX_IMAGES = 8;
export const AUDIO_IMAGE_MAX_BYTES = 3 * 1024 * 1024;

export type AudioSpeed = "slow" | "normal" | "fast";
export type AudioSource = "upload" | "pasted_text" | "existing_content" | "sample";

export type AudioSegment = {
  speaker: string;
  text: string;
};

export type AudioVoice = {
  id: string;
  name: string;
  gatewayVoice: string;
  presentation: "lower" | "higher";
  locale: "en-US";
  accent: "American English";
  description: string;
  previewText: string;
};

export const AUDIO_VOICES: readonly AudioVoice[] = [
  {
    id: "TxGEqnHWrfWFTfGW9XjX",
    name: "Roger",
    gatewayVoice: "Roger",
    presentation: "lower",
    locale: "en-US",
    accent: "American English",
    description: "Clear and steady for dialogue.",
    previewText: "Listen once for the main idea, then compare with your partner.",
  },
  {
    id: "Yko7PKHZNXotIFUBG7I9",
    name: "George",
    gatewayVoice: "George",
    presentation: "lower",
    locale: "en-US",
    accent: "American English",
    description: "Measured and composed for narration.",
    previewText: "The class opened their books and listened for the key details.",
  },
  {
    id: "EXAVITQu4vr4xnSDxMaL",
    name: "Sarah",
    gatewayVoice: "Sarah",
    presentation: "higher",
    locale: "en-US",
    accent: "American English",
    description: "Warm and expressive for conversations.",
    previewText: "Good morning. Are you ready to practise today’s dialogue?",
  },
  {
    id: "LcfcDJNUP1GQjkzn1xUU",
    name: "Laura",
    gatewayVoice: "Laura",
    presentation: "higher",
    locale: "en-US",
    accent: "American English",
    description: "Natural and calm for classroom listening.",
    previewText: "Take a moment to read the question before you listen again.",
  },
] as const;

export const DEFAULT_LOWER_VOICE_ID = AUDIO_VOICES[0].id;
export const DEFAULT_HIGHER_VOICE_ID = AUDIO_VOICES[2].id;

export const AUDIO_SAMPLE_TITLE = "Planning a class project";
export const AUDIO_SAMPLE_SEGMENTS: readonly AudioSegment[] = [
  {
    speaker: "Teacher",
    text: "Good morning. Today, we are going to plan a class project about our neighbourhood.",
  },
  {
    speaker: "Student",
    text: "Can we work in groups and choose a place to describe?",
  },
  {
    speaker: "Teacher",
    text: "Yes. Choose one place, explain why it is useful, and prepare three sentences together.",
  },
  {
    speaker: "Student",
    text: "Great. Our group would like to describe the local library.",
  },
] as const;

export const AUDIO_SAMPLE_TEXT = AUDIO_SAMPLE_SEGMENTS.map(
  (segment) => `${segment.speaker}: ${segment.text}`,
).join("\n");

export function getAudioVoice(voiceId: string): AudioVoice | undefined {
  return AUDIO_VOICES.find((voice) => voice.id === voiceId);
}

export function getGatewayAudioVoice(voiceId: string): string | undefined {
  return getAudioVoice(voiceId)?.gatewayVoice;
}

export function isSupportedAudioVoice(voiceId: string): boolean {
  return getAudioVoice(voiceId) !== undefined;
}

export function getDefaultVoiceForSpeaker(
  index: number,
  presentation?: "lower" | "higher",
): string {
  const matching = AUDIO_VOICES.filter(
    (voice) => presentation === undefined || voice.presentation === presentation,
  );
  const pool = matching.length > 0 ? matching : AUDIO_VOICES;
  return pool[index % pool.length]?.id ?? DEFAULT_LOWER_VOICE_ID;
}

export function normalizeAudioSegments(segments: readonly AudioSegment[]): AudioSegment[] {
  if (segments.length === 0) {
    throw new Error("Add at least one spoken line.");
  }
  if (segments.length > AUDIO_MAX_SEGMENTS) {
    throw new Error(`Keep the script to ${AUDIO_MAX_SEGMENTS} lines or fewer.`);
  }

  const normalized = segments.map((segment) => ({
    speaker: segment.speaker.replace(/\s+/g, " ").trim().slice(0, 40),
    text: segment.text.replace(/\r\n?/g, "\n").trim(),
  }));

  for (const segment of normalized) {
    if (!segment.speaker) {
      throw new Error("Every line needs a speaker or Narrator.");
    }
    if (!segment.text) {
      throw new Error("Remove empty lines or add the text to be spoken.");
    }
    if (segment.text.length > 1_500) {
      throw new Error("Split long passages into shorter narration blocks.");
    }
  }

  const characterCount = normalized.reduce(
    (total, segment) => total + segment.text.length,
    0,
  );
  if (characterCount > AUDIO_TEXT_MAX_CHARACTERS) {
    throw new Error(
      `Keep the script under ${AUDIO_TEXT_MAX_CHARACTERS.toLocaleString("en")} characters.`,
    );
  }

  return normalized;
}

export function parsePastedAudioText(input: string): {
  type: "dialogue" | "reading";
  segments: AudioSegment[];
} {
  const text = input.replace(/\r\n?/g, "\n").trim();
  if (!text) {
    throw new Error("Paste a dialogue, reading passage, or listening script to continue.");
  }
  if (text.length > AUDIO_TEXT_MAX_CHARACTERS) {
    throw new Error(
      `Keep pasted text under ${AUDIO_TEXT_MAX_CHARACTERS.toLocaleString("en")} characters.`,
    );
  }

  const lines = text.split("\n");
  const labelledLine = /^([^:\n]{1,40}):\s*(.+)$/;
  const hasSpeakerLabels = lines.some((line) => labelledLine.test(line.trim()));

  if (!hasSpeakerLabels) {
    return {
      type: "reading",
      segments: normalizeAudioSegments([
        { speaker: "Narrator", text: lines.map((line) => line.trim()).join("\n") },
      ]),
    };
  }

  const segments: AudioSegment[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(labelledLine);
    if (match) {
      segments.push({ speaker: match[1]!.trim(), text: match[2]!.trim() });
      continue;
    }
    const previous = segments.at(-1);
    if (previous) {
      previous.text = `${previous.text}\n${line}`;
    } else {
      segments.push({ speaker: "Narrator", text: line });
    }
  }

  return { type: "dialogue", segments: normalizeAudioSegments(segments) };
}

export function estimateAudioDurationSeconds(
  segments: readonly AudioSegment[],
  speed: AudioSpeed,
): number {
  const words = segments.reduce(
    (total, segment) => total + segment.text.trim().split(/\s+/).filter(Boolean).length,
    0,
  );
  if (words === 0) return 0;
  const wordsPerMinute = speed === "slow" ? 110 : speed === "fast" ? 150 : 130;
  const speakingSeconds = (words / wordsPerMinute) * 60;
  const pauseSeconds = Math.max(0, segments.length - 1) * 0.35;
  return Math.max(1, Math.round(speakingSeconds + pauseSeconds));
}
